import { getCurrentColor, getCurrentColorName, PREV_DEFECT_STYLE } from '@/app/constant'
import { useCanvasStore } from '@/app/store/canvas'
import { useAllTaskStore } from '@/app/store/task'
import { getDefaultBalloonColors, getRepairBalloonColors } from '@/app/function/helper'

const PURPLE = getCurrentColor('purple')
// 서버 기본색: savedCurrentStyles 복원 시 보라색 대체용
const DEFAULT_NON_PURPLE = getCurrentColor('red')

export interface SavedCurrentStyles {
  // 색상 관련 (전차 ↔ 현차 전환 시 사용)
  color?: string
  shapeDataColor?: string
  balloonFontColor?: string
  balloonFillColor?: string
  // 스케일 관련 (번호풍선 가리기 시 사용)
  balloonScale?: number
  connectedLineThicknessScale?: number
  balloonFontScale?: number
}

// shape_data에 savedCurrentStyles를 읽고 쓸 수 있는 최소 인터페이스
interface ShapeWithColorData {
  color: string
  progressing?: number
  shape_data: ShapeDataWithSavedStyles
}

interface ShapeDataWithSavedStyles {
  color?: string
  balloonFontColor?: string
  balloonFillColor?: string
  savedCurrentStyles?: SavedCurrentStyles
  [key: string]: unknown
}

// 현재 색상 스타일을 shape_data.savedCurrentStyles에 저장 (전차 전환 전 호출)
// 기존 스케일 필드를 보존하면서 색상 필드만 병합
// 보라색 값은 저장하지 않음 — 기존 savedCurrentStyles 값 유지
export function saveCurrentStylesToShapeData(shape: {
  color: string
  shape_data: ShapeDataWithSavedStyles
}): void {
  if (getCurrentColorName(shape.color) === 'purple') return
  // shape_data가 null인 레거시 결함 방어 (consumeSavedCurrentStyles와 동일 규칙)
  if (!shape.shape_data) return
  const prev = shape.shape_data.savedCurrentStyles
  shape.shape_data.savedCurrentStyles = {
    ...prev,
    color: shape.color,
    shapeDataColor:
      (shape.shape_data.color ?? '') === PURPLE
        ? (prev?.shapeDataColor ?? '')
        : (shape.shape_data.color ?? ''),
    balloonFontColor:
      (shape.shape_data.balloonFontColor ?? '') === PURPLE
        ? (prev?.balloonFontColor ?? '')
        : (shape.shape_data.balloonFontColor ?? ''),
    balloonFillColor: shape.shape_data.balloonFillColor ?? '',
  }
}

// shape_data.savedCurrentStyles에서 색상 필드만 읽고 삭제 (현차 복원 시 호출)
// 다른 기능(번호풍선 가리기 등)의 필드는 보존
// 보라색 값이 들어있으면 기본색(빨강)으로 대체
export function consumeSavedCurrentStyles(shape: {
  shape_data: ShapeDataWithSavedStyles
}): SavedCurrentStyles | undefined {
  const saved = shape.shape_data?.savedCurrentStyles
  if (!saved || saved.color === undefined) return undefined

  const safePurple = (v: string | undefined): string | undefined =>
    v === PURPLE ? DEFAULT_NON_PURPLE : v

  const colorStyles: SavedCurrentStyles = {
    color: safePurple(saved.color),
    shapeDataColor: safePurple(saved.shapeDataColor),
    balloonFontColor: safePurple(saved.balloonFontColor),
    balloonFillColor: saved.balloonFillColor,
  }

  delete saved.color
  delete saved.shapeDataColor
  delete saved.balloonFontColor
  delete saved.balloonFillColor

  if (Object.keys(saved).length === 0) {
    delete shape.shape_data.savedCurrentStyles
  }

  return colorStyles
}

export interface SavedBalloonScales {
  balloonScale: number
  connectedLineThicknessScale: number
  balloonFontScale: number
}

// 번호풍선 가리기: 현재 스케일값을 savedCurrentStyles에 저장
export function saveBalloonHideScales(shape: { shape_data: ShapeDataWithSavedStyles }): void {
  shape.shape_data.savedCurrentStyles = {
    ...shape.shape_data.savedCurrentStyles,
    balloonScale: (shape.shape_data.balloonScale as number) ?? 1,
    connectedLineThicknessScale: (shape.shape_data.connectedLineThicknessScale as number) ?? 1,
    balloonFontScale: (shape.shape_data.balloonFontScale as number) ?? 1,
  }
}

// 번호풍선 가리기 해제: 저장된 스케일값을 읽고 삭제
export function restoreBalloonHideScales(shape: {
  shape_data: ShapeDataWithSavedStyles
}): SavedBalloonScales | undefined {
  const saved = shape.shape_data?.savedCurrentStyles
  if (!saved || saved.balloonScale === undefined) return undefined

  const scales: SavedBalloonScales = {
    balloonScale: saved.balloonScale,
    connectedLineThicknessScale: saved.connectedLineThicknessScale ?? 1,
    balloonFontScale: saved.balloonFontScale ?? 1,
  }

  delete saved.balloonScale
  delete saved.connectedLineThicknessScale
  delete saved.balloonFontScale

  if (Object.keys(saved).length === 0) {
    delete shape.shape_data.savedCurrentStyles
  }

  return scales
}

// 번호풍선이 가려진 상태인지 확인
export function hasBalloonHideScales(shape: { shape_data: ShapeDataWithSavedStyles }): boolean {
  return shape.shape_data?.savedCurrentStyles?.balloonScale !== undefined
}

export interface BecomeColors {
  borderColor: string
  fontColor: string
  fillColor: string
}

/**
 * 전차 → 현재차가된전차 전환 시 적용할 색상 4값 계산. shape의 savedCurrentStyles는 mutation됨
 * (progressing=2면 색상 필드 폐기, 아니면 consume).
 *
 * 우선순위:
 *   1. progressing=2 (보수완료) → 보수완료색 또는 기본색
 *   2. savedCurrentStyles 존재 → 그 색상 복원
 *   3. 그 외 → getCurrentCreateStyles 기본색
 */
export function calcBecomeCurrentColors<T extends ShapeWithColorData>(shape: T): BecomeColors {
  if (Number(shape.progressing) === 2) {
    const config = useAllTaskStore().userSettingConfig
    const colors = getRepairBalloonColors(config) ?? getDefaultBalloonColors(config)
    // savedCurrentStyles의 색상 필드 폐기 (스케일 필드는 보존)
    const saved = shape.shape_data?.savedCurrentStyles
    if (saved) {
      delete saved.color
      delete saved.shapeDataColor
      delete saved.balloonFontColor
      delete saved.balloonFillColor
      if (Object.keys(saved).length === 0) {
        delete shape.shape_data.savedCurrentStyles
      }
    }
    return {
      borderColor: colors.borderColor,
      fontColor: colors.fontColor,
      fillColor: colors.fillColor,
    }
  }
  const restored = consumeSavedCurrentStyles(shape)
  if (restored && restored.color) {
    return {
      borderColor: restored.color,
      fontColor: restored.balloonFontColor ?? '',
      fillColor: restored.balloonFillColor ?? '',
    }
  }
  const styles = useCanvasStore().getCurrentCreateStyles()
  return {
    borderColor: styles.balloon_border_color,
    fontColor: styles.balloon_font_color,
    fillColor: styles.balloon_fill_color,
  }
}

/**
 * 현재차가된전차 → 전차 전환 시 적용할 색상 4값 (PREV_DEFECT_STYLE).
 * shape의 savedCurrentStyles에 현재 색상을 보존 (보라색이면 저장 안 함).
 */
export function calcBecomePreviousColors<T extends ShapeWithColorData>(shape: T): BecomeColors {
  saveCurrentStylesToShapeData(shape)
  return {
    borderColor: PREV_DEFECT_STYLE.color,
    fontColor: PREV_DEFECT_STYLE.balloonFontColor,
    fillColor: PREV_DEFECT_STYLE.balloonFillColor,
  }
}
